import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import { useCallback, useMemo } from 'react';

import useWeekDays from '@/components/ui/daisy/templates/week-calendar/useWeekDays';
import { toDayKey } from '@/components/ui/daisy/templates/week-calendar/weekColumns';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useKlassesPendingSummaryMap from '@/data/hook/shared/useKlassesPendingSummaryMap';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { scheduledLessonQueries } from '@/data/query-keys/scheduledLessonQueryKey';

import { ScheduledLessonWeekCalendarFilters } from './types';

const DAY_PAGE_SIZE = 10;

type UseScheduledLessonWeekCalendarProps = {
  filters?: ScheduledLessonWeekCalendarFilters;
  referenceDate?: Date;
};

export default function useScheduledLessonWeekCalendar({
  filters,
  referenceDate,
}: UseScheduledLessonWeekCalendarProps) {
  const { weekDays } = useWeekDays(referenceDate);

  const {
    results,
    isInitialLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteService({
    ...scheduledLessonQueries.weekCalendar({
      ...filters,
      ordering: 'datetime',
      isActive: true,
      pageSize: DAY_PAGE_SIZE,
      dateRangeAfter: toDayKey(weekDays[0].date),
      dateRangeBefore: toDayKey(weekDays[weekDays.length - 1].date),
    }),
    keepPreviousData: true,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const lessons = useMemo(
    () => results.flatMap(({ scheduledLessons }) => scheduledLessons),
    [results],
  );

  const lessonsByColumn = useMemo(() => {
    const pagesByColumn = mapValues(groupBy(results, 'id'), columns =>
      columns.flatMap(({ scheduledLessons }) => scheduledLessons),
    );

    return weekDays.map((_, index) => pagesByColumn[index] ?? []);
  }, [results, weekDays]);

  const rowCount = useMemo(
    () => Math.max(0, ...lessonsByColumn.map(({ length }) => length)),
    [lessonsByColumn],
  );

  const klassIds = useMemo(
    () => lessons.map(lesson => lesson.klass),
    [lessons],
  );

  const { klassesPendingSummaryMap, isLoading: isLoadingPendingSummary } =
    useKlassesPendingSummaryMap({
      klassIds,
    });

  const handleRowsRendered = useCallback(
    ({ stopIndex }: { stopIndex: number }) => {
      if (!hasNextPage || isFetchingNextPage) return;
      if (stopIndex >= rowCount - 1) fetchNextPage();
    },
    [hasNextPage, isFetchingNextPage, rowCount, fetchNextPage],
  );

  return {
    weekDays,
    lessonsByColumn,
    rowCount,
    klassesPendingSummaryMap,
    loading: isInitialLoading,
    loadingPendingSummary: isLoadingPendingSummary,
    isEmpty: !isInitialLoading && lessons.length === 0,
    isFetchingNextPage,
    handleRowsRendered,
  };
}
